import { cx } from "@jugl-web/utils";
import { isColorDark } from "@jugl-web/utils/color";
import { FC, ReactNode, forwardRef } from "react";

export const AVATAR_SIZE = 20;
export const AVATAR_BUBBLE_OFFSET = 8;

interface ParticipantAvatarProps {
  name: string;
  imageUrl?: string;
}

const SenderAvatar: FC<ParticipantAvatarProps> = ({ name, imageUrl }) => {
  const character = name.charAt(0).toUpperCase();

  return (
    <div
      className="flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-200"
      style={{ width: AVATAR_SIZE, height: AVATAR_SIZE }}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={character}
          className="h-full w-full rounded-full object-cover p-px"
        />
      ) : (
        <span className="font-secondary text-[11px] text-black">
          {character}
        </span>
      )}
    </div>
  );
};

export interface ChatMessageBubbleProps {
  children: ReactNode;
  isOutgoing: boolean;
  userBubbleColor: string;
  senderName: string;
  senderImageUrl?: string;
  title?: string;
}

export const ChatMessageBubble = forwardRef<
  HTMLDivElement,
  ChatMessageBubbleProps
>(
  (
    {
      children,
      isOutgoing,
      userBubbleColor,
      senderName,
      senderImageUrl,
      title,
    },
    ref
  ) => {
    const isTextColorLight = isColorDark(userBubbleColor);

    return (
      <div
        ref={ref}
        className={cx(
          "flex max-w-[85%] items-end",
          isOutgoing && "flex-row-reverse"
        )}
        style={{ gap: AVATAR_BUBBLE_OFFSET }}
      >
        <SenderAvatar name={senderName} imageUrl={senderImageUrl} />
        {/* Bubble */}
        <div
          className={cx(
            "overflow-hidden px-4 py-3",
            isOutgoing
              ? "rounded-l-[20px] rounded-tr-[20px] rounded-br-[5px]"
              : "rounded-tl-[20px] rounded-r-[20px] rounded-bl-[5px]"
          )}
          title={title}
          style={{
            backgroundColor: isOutgoing ? userBubbleColor : "#F9F9F9",
          }}
        >
          {/* Sender name (only shown for incoming messages) */}
          {!isOutgoing && (
            <div
              className={cx(
                "truncate font-secondary text-[13px] leading-[18px] font-medium",
                {
                  "text-white/80": isOutgoing && isTextColorLight,
                  "text-black/60": isOutgoing && !isTextColorLight,
                  "text-[#737B7E]": !isOutgoing,
                }
              )}
            >
              {senderName}
            </div>
          )}
          {/* Bubble body */}
          {children}
        </div>
      </div>
    );
  }
);

ChatMessageBubble.displayName = "ChatMessageBubble";
